import { type GetUsersResponse } from "@mg/schemas/src/christo/catalyst";
import {
  EnterpriseOnboardingStatus,
  EnterpriseProfileType,
} from "@mg/schemas/src/commons/user";
import {
  CaretDown,
  CheckCircle,
  MagnifyingGlass,
  PaperPlaneRight,
  Plus,
  Question,
  Trash,
  Note,
  ArrowClockwise,
  DotsThree,
  UserMinus,
  ArrowCounterClockwise,
  Eraser,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Dialog,
  DropdownMenu,
  Flex,
  Heading,
  IconButton,
  Popover,
  Select,
  Table,
  Text,
  TextArea,
  TextField,
  Tooltip,
  Grid,
} from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";
import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";
import { useUI } from "../../../contexts/ui";
import { errorAnalyticsPayload, useAnalytics } from "../../../utils/analytics";
import {
  canArchiveUsers,
  canManageUsers,
  requiresAuth,
} from "../../../utils/auth";
import { errorMessage } from "../../../utils/http";
import {
  useArchiveUser,
  useArchiveMeaningfulGigsUser,
  useInviteUser,
  useUserRoleMutation,
  useGetMeaningfulGigsUsers,
  useAddUserToEnterprise,
  useSyncDefaultMgUsers,
  useBackfillPunttAccess,
  useRollbackPunttAccess,
  useResendInvite,
  useRemoveUserFromEnterprise,
  useRestoreUserToEnterprise,
  useGetRemovedUsers,
  useGetReviewerProfiles,
} from "../../../utils/queries/users";
import { store } from "../../../utils/store";
import { useGetVisibleUsers } from "../../../utils/tldraw/comments";
import { authLayoutRoute } from "../../auth-layout/route";
import { ticketsRoute } from "../../tickets/route";
import { ResetCustomReviewDialog } from "../components/ResetCustomReviewDialog";
import { UserAdminNoteDialog } from "../components/UserAdminNoteDialog";

export const usersRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "users",
  beforeLoad() {
    const canAccess = canManageUsers();

    if (!canAccess) {
      throw redirect({
        to: ticketsRoute.to,
      } as any);
    }

    requiresAuth();
  },
  component: Users,
});

export function Users() {
  const { t } = useTranslation("ui");
  const userRoleMutation = useUserRoleMutation();
  const posthog = useAnalytics("Users");
  const { notify } = useUI();

  const [filters, setFilters] = useState<{ name: string }>({ name: "" });

  const { value: currentUser } = store.getState().auth;
  // Role OR CONNECT_TEAM — JWT `permissions` can omit CONNECT_TEAM for
  // client-type ADMIN/MG sessions even though the archive APIs allow them.
  const canArchive = canArchiveUsers();

  // Check if user has access to any actions
  const hasAnyActionAccess = canArchive;

  const [addUserName, setAddUserName] = useState("");
  const [addUserEmail, setAddUserEmail] = useState("");
  const [addUserDropdownOpen, setAddUserDropdownOpen] = useState(false);
  const [bulkImportUserCsv, setBulkImportUserCsv] = useState("");
  const [isBulkImporting, setIsBulkImporting] = useState(false);

  const closeNewUserDialogRef = useRef<HTMLButtonElement>(null);
  const closeBulkImportUserDialogRef = useRef<HTMLButtonElement>(null);

  const {
    data: unsortedUsers,
    refetch,
    isPending,
    isRefetching,
  } = useGetVisibleUsers(false);
  const users = useMemo(() => {
    return unsortedUsers
      ?.filter(
        (user) =>
          !filters.name ||
          user.name?.toLowerCase().includes(filters.name.toLowerCase()) ||
          user.email?.toLowerCase().includes(filters.name.toLowerCase()),
      )
      .toSorted((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [unsortedUsers, filters.name]);

  // Get meaningful gigs users not in enterprise
  const {
    data: meaningfulGigsUsers,
    refetch: refetchMeaningfulGigsUsers,
    isPending: isMeaningfulGigsUsersPending,
    isRefetching: isMeaningfulGigsUsersRefetching,
  } = useGetMeaningfulGigsUsers();

  const archiveUserMutation = useArchiveUser();
  const archiveMeaningfulGigsUserMutation = useArchiveMeaningfulGigsUser();
  const inviteUserMutation = useInviteUser();
  const addUserToEnterpriseMutation = useAddUserToEnterprise();
  const syncDefaultMgUsersMutation = useSyncDefaultMgUsers();
  const backfillPunttAccessMutation = useBackfillPunttAccess();
  const rollbackPunttAccessMutation = useRollbackPunttAccess();
  const [isBackfillDialogOpen, setIsBackfillDialogOpen] = useState(false);
  const [isRollbackDialogOpen, setIsRollbackDialogOpen] = useState(false);
  const resendInviteMutation = useResendInvite();
  const removeFromEnterpriseMutation = useRemoveUserFromEnterprise();
  const restoreToEnterpriseMutation = useRestoreUserToEnterprise();
  const [pendingActionUserId, setPendingActionUserId] = useState<string | null>(
    null,
  );
  const {
    data: removedUsers,
    refetch: refetchRemovedUsers,
    isPending: isRemovedUsersPending,
  } = useGetRemovedUsers();

  const [removeDialogUser, setRemoveDialogUser] = useState<
    GetUsersResponse[number] | null
  >(null);
  const [archiveEverywhereDialogUser, setArchiveEverywhereDialogUser] =
    useState<GetUsersResponse[number] | null>(null);

  const isLoading =
    isPending ||
    isRefetching ||
    archiveUserMutation.isPending ||
    inviteUserMutation.isPending;

  const isMeaningfulGigsUsersLoading =
    isMeaningfulGigsUsersPending ||
    isMeaningfulGigsUsersRefetching ||
    addUserToEnterpriseMutation.isPending ||
    archiveMeaningfulGigsUserMutation.isPending ||
    syncDefaultMgUsersMutation.isPending;

  const loader = (
    <Table.Row>
      <Table.Cell colSpan={4} className="text-center">
        {t("settings.usersRoute.loadingUsers")}
      </Table.Cell>
    </Table.Row>
  );

  const meaningfulGigsUsersLoader = (
    <Table.Row>
      <Table.Cell colSpan={3} className="text-center">
        {t("settings.usersRoute.loadingMeaningfulGigsUsers")}
      </Table.Cell>
    </Table.Row>
  );

  let allowedRoles: EnterpriseProfileType[] = [];
  const roles = [
    EnterpriseProfileType.CATALYST_REQUESTER,
    EnterpriseProfileType.OPS,
    EnterpriseProfileType.EXPERT,
    EnterpriseProfileType.COORDINATOR,
    EnterpriseProfileType.ADMIN,
    EnterpriseProfileType.ADMIN_WITH_AI,
  ];
  if (currentUser?.role === EnterpriseProfileType.MEANINGFUL_GIGS) {
    roles.push(EnterpriseProfileType.CATALYST_AI);
    roles.push(EnterpriseProfileType.MEANINGFUL_GIGS);
    allowedRoles = roles;
  } else if (
    currentUser?.role === EnterpriseProfileType.ADMIN ||
    currentUser?.role === EnterpriseProfileType.ADMIN_WITH_AI
  ) {
    allowedRoles = roles;
  }

  function handleAddUserToEnterprise(userId: string, userName: string) {
    addUserToEnterpriseMutation.mutate(
      { targetUserId: userId },
      {
        onSuccess: () => {
          refetch();
          refetchMeaningfulGigsUsers();
          notify({
            variant: "success",
            title: `${userName} successfully added to enterprise`,
            message: "User has been added to the enterprise.",
            leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
          });
        },
        onError: (error) => {
          notify({
            variant: "error",
            title: `Failed to add ${userName} to enterprise`,
            message: error.message,
          });
        },
      },
    );
  }

  function handleSyncDefaultMgUsers() {
    syncDefaultMgUsersMutation.mutate(undefined, {
      onSuccess: (data) => {
        refetch();
        refetchMeaningfulGigsUsers();
        const parts: string[] = [];
        if (data.added.length > 0) parts.push(`Added ${data.added.length}`);
        if (data.skipped.length > 0)
          parts.push(`${data.skipped.length} already in enterprise`);
        if (data.notFound.length > 0)
          parts.push(`${data.notFound.length} not found`);
        notify({
          variant: "success",
          title: "Default MG users synced",
          message: parts.join(". ") || "All users already in enterprise.",
          leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
        });
      },
      onError: (error) => {
        notify({
          variant: "error",
          title: "Failed to sync default MG users",
          message: error.message,
        });
      },
    });
  }

  function handleBackfillPunttAccess() {
    backfillPunttAccessMutation.mutate(undefined, {
      onSuccess: (data) => {
        setIsBackfillDialogOpen(false);
        refetch();
        refetchMeaningfulGigsUsers();
        notify({
          variant: "success",
          title: "Puntt access backfilled",
          message: `Added ${data.profilesAdded.toLocaleString()} ${
            data.profilesAdded === 1 ? "profile" : "profiles"
          } across ${data.enterprisesTargeted.toLocaleString()} ${
            data.enterprisesTargeted === 1 ? "enterprise" : "enterprises"
          }.`,
          leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
        });
      },
      onError: (error) => {
        notify({
          variant: "error",
          title: "Failed to backfill Puntt access",
          message: error.message,
        });
      },
    });
  }

  function handleRollbackPunttAccess() {
    rollbackPunttAccessMutation.mutate(undefined, {
      onSuccess: (data) => {
        setIsRollbackDialogOpen(false);
        refetch();
        refetchMeaningfulGigsUsers();
        notify({
          variant: "success",
          title: "Puntt access rolled back",
          message: `Removed ${data.profilesRemoved.toLocaleString()} ${
            data.profilesRemoved === 1 ? "profile" : "profiles"
          } from ${data.enterprisesCleaned.toLocaleString()} orphan ${
            data.enterprisesCleaned === 1 ? "enterprise" : "enterprises"
          }.`,
          leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
        });
      },
      onError: (error) => {
        notify({
          variant: "error",
          title: "Failed to rollback Puntt access",
          message: error.message,
        });
      },
    });
  }

  function handleArchiveMeaningfulGigsUser(userId: string, userName: string) {
    archiveMeaningfulGigsUserMutation.mutate(userId, {
      onSuccess: () => {
        refetchMeaningfulGigsUsers();
        notify({
          variant: "success",
          title: `${userName} successfully archived`,
          message:
            "User has been archived and removed from the internal admin list.",
          leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
        });
      },
      onError: (error) => {
        notify({
          variant: "error",
          title: `Failed to archive ${userName}`,
          message: error.message,
        });
      },
    });
  }

  function friendlyRole(role?: EnterpriseProfileType) {
    if (!role) return "";
    if (role === EnterpriseProfileType.OPS) {
      return "Normal";
    }
    if (role === EnterpriseProfileType.CATALYST_REQUESTER) {
      return "Restricted";
    }
    if (role === EnterpriseProfileType.CATALYST_AI) {
      return "AI";
    }
    if (role === EnterpriseProfileType.ADMIN_WITH_AI) {
      return "Admin With AI";
    }
    return role
      .replace(/-/g, " ")
      .replace(
        /\w\S*/g,
        (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
      );
  }

  // Resolve the onboarding status of a user *for this enterprise*. We can't
  // assume the user object is fully populated — share-link INVITED guests
  // arrive with only `email` + a single CATALYST_REQUESTER profile and no
  // `name`/`avatar`/etc. Returning a defensive `undefined` when fields are
  // missing keeps the row from crashing the page.
  function getOnboardingStatus(
    user: GetUsersResponse[number],
  ): EnterpriseOnboardingStatus | undefined {
    const profile = user?.enterpriseProfiles?.find(
      (p) => p?.enterprise === currentUser?.enterpriseId,
    );
    return profile?.onboarding as EnterpriseOnboardingStatus | undefined;
  }

  // Map onboarding status -> { label, badge color }. Uses Radix `Badge`
  // colour tokens already in the design system; no new tokens introduced.
  function statusBadge(status?: EnterpriseOnboardingStatus): {
    label: string;
    color: React.ComponentProps<typeof Badge>["color"];
  } {
    switch (status) {
      case EnterpriseOnboardingStatus.ONBOARDED:
        return { label: "Active", color: "green" };
      case EnterpriseOnboardingStatus.INVITED:
        return { label: "Invited", color: "amber" };
      case EnterpriseOnboardingStatus.IMPORTED:
        return { label: "Imported", color: "blue" };
      case EnterpriseOnboardingStatus.RECOMMENDED:
        return { label: "Recommended", color: "indigo" };
      case EnterpriseOnboardingStatus.ARCHIVED:
        return { label: "Archived", color: "gray" };
      case EnterpriseOnboardingStatus.REMOVED:
        return { label: "Removed", color: "red" };
      default:
        return { label: "Unknown", color: "gray" };
    }
  }

  function saveNewUserHandler(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const startTime = performance.now();

    // All invites from this page now create the user as Normal (OPS). The
    // role selector was removed; the christo invite handler also forces this
    // role server-side so a crafted request can't escalate.
    const inviteName = addUserName.trim();
    const inviteLabel = inviteName || addUserEmail;

    inviteUserMutation.mutate(
      {
        enterpriseId: currentUser?.enterpriseId as string,
        name: inviteName,
        email: addUserEmail,
        role: EnterpriseProfileType.OPS,
      },
      {
        onSuccess: (data) => {
          refetch();

          posthog.capture("user_invited", {
            userId: data?._id,
            userEmail: addUserEmail,
            userName: inviteName,
            userRole: EnterpriseProfileType.OPS,
            durationSeconds: (performance.now() - startTime) / 1000,
          });

          closeNewUserDialogRef.current?.click();

          notify({
            variant: "success",
            title: `${inviteLabel} successfully invited`,
            message: "User has been invited to the platform.",
            leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
          });

          setAddUserName("");
          setAddUserEmail("");
        },
        onError: (error) => {
          posthog.capture("user_invited_error", {
            userEmail: addUserEmail,
            userName: inviteName,
            userRole: EnterpriseProfileType.OPS,
            durationSeconds: (performance.now() - startTime) / 1000,
            ...errorAnalyticsPayload(error),
          });

          notify({
            variant: "error",
            title: `Failed to invite ${inviteLabel}`,
            message: error.message,
          });

          throw error;
        },
      },
    );
  }

  function newUserForm() {
    return (
      <form
        onSubmit={saveNewUserHandler}
        data-testid="new-user-form"
        aria-label={t("settings.usersRoute.addNewUser")}
      >
        <Flex direction="column" gap="3">
          <label htmlFor="new-user-name">
            <Text as="div" size="2" mb="1" weight="bold">
              {t("settings.usersRoute.nameOptional")}
            </Text>
            <TextField.Root
              id="new-user-name"
              placeholder={t("settings.usersRoute.enterName")}
              value={addUserName}
              onChange={(e) => setAddUserName(e.target.value)}
            />
          </label>
          <label htmlFor="new-user-email">
            <Text as="div" size="2" mb="1" weight="bold">
              {t("common:labels.email")}
            </Text>
            <TextField.Root
              id="new-user-email"
              placeholder={t("settings.usersRoute.enterEmail")}
              value={addUserEmail}
              onChange={(e) => setAddUserEmail(e.target.value)}
              required
            />
          </label>
        </Flex>
        <Flex gap="2" mt="4" justify="end">
          <Dialog.Close
            data-testid="add-user-cancel"
            onClick={(e) => e.stopPropagation()}
            ref={closeNewUserDialogRef}
          >
            <Button variant="soft" color="gray">
              {t("common:actions.cancel")}
            </Button>
          </Dialog.Close>
          <Button
            type="submit"
            data-testid="add-user-confirm"
            disabled={inviteUserMutation.isPending}
            loading={inviteUserMutation.isPending}
          >
            <PaperPlaneRight />
            Send Invitation
          </Button>
        </Flex>
      </form>
    );
  }

  async function bulkImportUserHandler(
    event: React.FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    const startTime = performance.now();
    setIsBulkImporting(true);
    setAddUserDropdownOpen(false);

    const csvRows = bulkImportUserCsv.trim().split("\n");
    if (!csvRows.length) {
      notify({
        variant: "error",
        title: "No users to import",
        message: "Please enter a list of users to import.",
      });
      return;
    }

    // Duplicate + new-domain detection reads the FULL roster, not `users` —
    // that one is the search-filtered view. With a filter typed in the search
    // box, an existing member whose row is filtered out looks brand new here,
    // so the import re-invites them: christo regenerates their one-time code
    // (invalidating the old one), flips their profile back to INVITED, and
    // emails them again.
    //
    // Both screens compare lowercased: mailbox addresses are not
    // case-sensitive in practice, and a CSV pasted out of a spreadsheet
    // routinely capitalizes them ("First.Last@Company.com"). christo's roster
    // lookup (UserService.findByEmail) is an exact Mongo match and the invite
    // schema only trims, so a case-sensitive compare here lets that row
    // through as a brand-new person — christo then creates a SECOND account
    // for an address that already has one, and counts the member's own domain
    // as new.
    const rosterEmails = new Set(
      unsortedUsers?.map((user) => user.email?.toLowerCase()) ?? [],
    );
    const domains = new Set(
      Array.from(rosterEmails).map((email) => email?.split("@")[1]),
    );

    // Error types
    const invalidRows = [];
    const newDomains = new Set<string>();
    const alreadyExists = [];
    const failed = [];

    const succeeded: string[] = [];
    for (const row of csvRows) {
      if (!row.trim()) continue;
      const [name, email] = row.split(",").map((part) => part.trim());
      if (!name && !email) continue;
      if (!name || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        invalidRows.push(row);
        continue;
      }
      const normalizedEmail = email.toLowerCase();
      const domain = normalizedEmail.split("@")[1];
      if (!domains.has(domain)) {
        newDomains.add(domain);
      }
      if (
        rosterEmails.has(normalizedEmail) ||
        succeeded.includes(normalizedEmail)
      ) {
        alreadyExists.push(`${name} <${email}>`);
        continue;
      }

      try {
        // To avoid sending too many invites at once, we only send one at a time
        await inviteUserMutation.mutateAsync({
          enterpriseId: currentUser?.enterpriseId as string,
          name,
          email,
          role: EnterpriseProfileType.OPS,
        });
        succeeded.push(normalizedEmail);
      } catch (error) {
        const message = await errorMessage(error);
        failed.push(`${name} <${email}>: ${message}`);
      }
    }

    // It would be nice to show these all at once.
    if (failed.length) {
      notify({
        variant: "error",
        title: `Error inviting ${failed.length.toLocaleString()} user${failed.length === 1 ? "" : "s"}`,
        message: failed.join("\n"),
      });
    }
    if (invalidRows.length) {
      notify({
        variant: "error",
        title: `${invalidRows.length.toLocaleString()} invalid user${invalidRows.length === 1 ? "" : "s"}`,
        message: invalidRows.join("\n"),
      });
    }
    if (newDomains.size) {
      notify({
        variant: "default",
        title: `Users invited from ${newDomains.size.toLocaleString()} new domain${newDomains.size === 1 ? "" : "s"}`,
        message: Array.from(newDomains).join("\n"),
      });
    }
    if (alreadyExists.length) {
      notify({
        variant: "default",
        title: `${alreadyExists.length.toLocaleString()} user${alreadyExists.length === 1 ? "" : "s"} already added`,
        message: alreadyExists.join("\n"),
      });
    }
    // Show the success message last, otherwise people might see a green toast and leave without seeing the other messages.
    if (succeeded.length) {
      notify({
        variant: "success",
        title: `${succeeded.length.toLocaleString()} user${succeeded.length === 1 ? "" : "s"} successfully invited`,
        // Not showing `message: succeeded.join("\n"),` because it could be very long
        leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
      });
    }

    posthog.capture("bulk_import_users", {
      role: EnterpriseProfileType.OPS,
      rows: csvRows.length,
      numSucceeded: succeeded.length,
      numFailed: failed.length,
      numInvalid: invalidRows.length,
      numNewDomains: newDomains.size,
      numAlreadyExists: alreadyExists.length,
      succeeded,
      failed,
      invalidRows,
      newDomains,
      alreadyExists,
      durationSeconds: (performance.now() - startTime) / 1000,
    });

    // Clean up
    refetch();
    setBulkImportUserCsv("");
    closeBulkImportUserDialogRef.current?.click();
    setIsBulkImporting(false);
  }

  function bulkImportUserForm() {
    return (
      <form
        onSubmit={bulkImportUserHandler}
        data-testid="bulk-import-user-form"
        aria-label={t("settings.usersRoute.bulkImportUsers")}
      >
        <Flex direction="column" gap="3">
          <label htmlFor="bulk-import-csv">
            <Text as="div" size="2" mb="1" weight="bold">
              {t("settings.usersRoute.nameEmail")}
            </Text>
            <TextArea
              id="bulk-import-csv"
              data-testid="bulk-import-csv-input"
              aria-label={t("settings.usersRoute.bulkImportUsersCsv")}
              placeholder={
                "First Last, name@example.com\nFirst Last, name@example.com"
              }
              value={bulkImportUserCsv}
              onChange={(e) => setBulkImportUserCsv(e.target.value)}
              required
            />
          </label>
        </Flex>
        <Flex gap="2" mt="4" justify="end">
          <Dialog.Close
            data-testid="bulk-import-cancel"
            onClick={(e) => e.stopPropagation()}
            ref={closeBulkImportUserDialogRef}
          >
            <Button variant="soft" color="gray">
              {t("common:actions.cancel")}
            </Button>
          </Dialog.Close>
          <Button
            type="submit"
            data-testid="bulk-import-confirm"
            disabled={inviteUserMutation.isPending || isBulkImporting}
            loading={inviteUserMutation.isPending || isBulkImporting}
          >
            <PaperPlaneRight />
            Send Invitations
          </Button>
        </Flex>
      </form>
    );
  }

  // Admin note about a user, saved on their enterprise profile for downstream
  // AI use. The row being annotated; null keeps the dialog closed.
  const [noteUser, setNoteUser] = useState<GetUsersResponse[number] | null>(
    null,
  );

  // Custom (Personalize) review reset. The menu item only renders for users
  // christo reports a reviewer profile for, so there is always something to
  // reset; the count feeds the dialog's "also remove documents" opt-in.
  const [resetReviewUser, setResetReviewUser] = useState<
    GetUsersResponse[number] | null
  >(null);
  const { data: reviewerProfiles, refetch: refetchReviewerProfiles } =
    useGetReviewerProfiles(canArchive);
  const reviewDocumentCounts = useMemo(
    () =>
      new Map(
        (reviewerProfiles?.profiles ?? []).map((profile) => [
          profile.userId,
          profile.documentCount,
        ]),
      ),
    [reviewerProfiles],
  );

  // Reset invite user dialog fields when closed
  useEffect(() => {
    if (!addUserDropdownOpen) {
      setAddUserName("");
      setAddUserEmail("");
      setBulkImportUserCsv("");
    }
  }, [addUserDropdownOpen]);

  return (
    <div
      className={cx("h-full overflow-auto px-8 py-2")}
      data-testid="users-page"
    >
      <Flex
        direction="row"
        align="center"
        justify="between"
        className="mb-2 px-2 py-4"
      >
        <Heading
          size="8"
          className="font-sans"
          data-testid="user-management-heading"
        >
          User Management
          {users?.length ? ` (${users.length.toLocaleString()} users)` : ""}
        </Heading>
        <Flex align="center" gap="2">
          <TextField.Root
            placeholder={t("settings.usersRoute.searchUsers")}
            size="1"
            defaultValue={filters.name}
            data-testid="users-search-input"
            onInput={({ target }) =>
              setFilters({ name: (target as HTMLInputElement).value })
            }
          >
            <TextField.Slot>
              <MagnifyingGlass />
            </TextField.Slot>
          </TextField.Root>
          <Popover.Root
            open={addUserDropdownOpen}
            onOpenChange={setAddUserDropdownOpen}
          >
            <Flex>
              <Dialog.Root>
                <Dialog.Trigger>
                  <Button
                    variant="solid"
                    size="1"
                    className="rounded-r-none"
                    data-testid="invite-user-button"
                  >
                    <Plus />
                    Invite User
                  </Button>
                </Dialog.Trigger>
                <Dialog.Content style={{ maxWidth: 350 }}>
                  <Dialog.Title>
                    {t("settings.usersRoute.inviteNewUser")}
                  </Dialog.Title>
                  <Dialog.Description>
                    Add a new user to your enterprise. They will receive an
                    email invitation to join.
                  </Dialog.Description>
                  {newUserForm()}
                </Dialog.Content>
              </Dialog.Root>
              <Popover.Trigger>
                <IconButton
                  size="1"
                  className="rounded-l-none border-0 border-l border-solid border-l-puntt-neutral-gray-10 transition-[border-radius] data-[state=open]:rounded-br-none"
                >
                  <CaretDown />
                </IconButton>
              </Popover.Trigger>
            </Flex>
            <Popover.Content
              align="end"
              sideOffset={0}
              className="rounded-sm rounded-tr-none p-0"
            >
              <Dialog.Root>
                <Dialog.Trigger>
                  <Button
                    variant="solid"
                    size="1"
                    className="rounded-tr-none"
                    data-testid="bulk-invite-users-button"
                  >
                    <Plus />
                    Bulk Invite Users
                  </Button>
                </Dialog.Trigger>
                <Dialog.Content style={{ maxWidth: 350 }}>
                  <Dialog.Title>
                    {t("settings.usersRoute.bulkInviteUsers")}
                  </Dialog.Title>
                  <Dialog.Description>
                    Invite multiple users at once. Each line should contain a
                    name and email, separated by a comma.
                  </Dialog.Description>
                  {bulkImportUserForm()}
                </Dialog.Content>
              </Dialog.Root>
            </Popover.Content>
          </Popover.Root>
        </Flex>
      </Flex>
      <Flex direction="column" className="px-2">
        <Text size="3" color="gray">
          Use this page to invite new users, assign or change their roles,
          archive users, and keep your team organized. You can search, filter,
          and edit user details.
          <br />
          <br />
          When users join through a shared file or folder, they start as
          Restricted. You can upgrade their role as needed. For advanced changes
          or support, contact Meaningful Gigs.
        </Text>
      </Flex>
      <Grid gap="4" px="2" py="4">
        <Table.Root
          variant="surface"
          className="w-full min-w-[300px]"
          data-testid="users-table"
        >
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell data-testid="column-header-name">
                {t("common:labels.name")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell data-testid="column-header-email">
                {t("common:labels.email")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell
                className="w-[140px]"
                data-testid="column-header-status"
              >
                <Flex align="center" justify="start" gap="1">
                  Status
                  <Tooltip
                    content={
                      <span>
                        <strong>{t("settings.usersRoute.active")}</strong> User
                        has finished onboarding.
                        <br />
                        <strong>{t("settings.usersRoute.invited")}</strong>{" "}
                        Invite sent (or share-link guest) — has not finished
                        onboarding yet.
                        <br />
                        <strong>
                          {t("settings.usersRoute.importedRecommended")}
                        </strong>{" "}
                        Added to the enterprise without going through
                        onboarding.
                      </span>
                    }
                  >
                    <Question
                      size={16}
                      weight="fill"
                      className="text-gray-500 inline-block align-middle"
                    />
                  </Tooltip>
                </Flex>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell
                className="w-[200px]"
                data-testid="column-header-role"
              >
                <Flex align="center" justify="start" gap="1">
                  Role
                  <Tooltip
                    content={
                      <span>
                        <strong>{t("settings.usersRoute.admin")}</strong>{" "}
                        Invite, archive, and change roles. Full file access, can
                        dismiss/resolve comments.
                        <br />
                        <strong>{t("settings.usersRoute.normal")}</strong>{" "}
                        Access all files, dismiss/resolve comments. Cannot
                        manage users or change roles.
                        <br />
                        <strong>{t("settings.usersRoute.expert")}</strong> Full
                        ticket access plus review and creative assignment.
                        Cannot manage users or change roles.
                        <br />
                        <strong>
                          {t("settings.usersRoute.coordinator")}
                        </strong>{" "}
                        Full ticket access plus review and creative assignment.
                        Cannot manage users or change roles.
                        <br />
                        <strong>
                          {t("settings.usersRoute.restricted")}
                        </strong>{" "}
                        Only access shared files, can resolve (not dismiss)
                        comments, cannot manage users or change roles.
                      </span>
                    }
                  >
                    <Question
                      size={16}
                      weight="fill"
                      className="text-gray-500 inline-block align-middle"
                    />
                  </Tooltip>
                </Flex>
              </Table.ColumnHeaderCell>
              {hasAnyActionAccess && (
                <Table.ColumnHeaderCell
                  className="w-[200px]"
                  data-testid="column-header-actions"
                >
                  <Flex align="center" justify="start" gap="1">
                    Actions
                    <Tooltip
                      content={
                        <span>
                          <strong>{t("settings.usersRoute.adminNote")}</strong>{" "}
                          Save a note about this user for Puntt to use later.
                          <br />
                          <strong>
                            {t("settings.usersRoute.archive")}
                          </strong>{" "}
                          Deactivate a user. Users cannot be invited again.
                        </span>
                      }
                    >
                      <Question
                        size={16}
                        weight="fill"
                        className="text-gray-500 inline-block align-middle"
                      />
                    </Tooltip>
                  </Flex>
                </Table.ColumnHeaderCell>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading && loader}
            {users?.map((user: GetUsersResponse[number]) => {
              const isMgUser =
                user?.email?.endsWith("@meaningfulgigs.com") ||
                user?.email?.endsWith("@puntt.ai") ||
                user?.role === EnterpriseProfileType.MEANINGFUL_GIGS;
              // `name` is optional on the users payload; never read "undefined"
              // into an accessible name. Truthiness, not `??`, so a blank
              // name falls back the same way as the dialog title and toast.
              const noteSubject = user.name || "this user";
              const onboardingStatus = getOnboardingStatus(user);
              const { label: statusLabel, color: statusColor } =
                statusBadge(onboardingStatus);
              return (
                <Table.Row
                  key={user._id}
                  className={
                    isMgUser
                      ? "bg-puntt-blue-1 hover:bg-puntt-blue-2"
                      : "bg-[#F9F9F9] hover:bg-carbon-50"
                  }
                  data-testid={`user-row-${user._id}`}
                >
                  <Table.Cell className="align-middle">
                    <AvatarWithInitials
                      avatar={user?.avatar}
                      name={user?.name}
                      colorKey={user?._id}
                      backgroundColor={user?.avatarBackgroundColor}
                      textColor={user?.avatarTextColor}
                      size={8}
                      square
                    />
                    <Text
                      weight="bold"
                      className="pl-2"
                      data-testid={`user-name-${user._id}`}
                    >
                      {user?.name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell
                    className="align-middle"
                    data-testid={`user-email-${user._id}`}
                  >
                    {user?.email}
                  </Table.Cell>
                  <Table.Cell className="align-middle">
                    <Badge
                      color={statusColor}
                      variant="soft"
                      data-testid={`user-status-${user._id}`}
                    >
                      {statusLabel}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="align-middle capitalize">
                    {currentUser?.userID === user._id ||
                    !allowedRoles.length ? (
                      <Text size="2" className="capitalize">
                        {friendlyRole(user.role)}
                      </Text>
                    ) : (
                      <Select.Root
                        value={user.role}
                        disabled={userRoleMutation.isPending}
                        onValueChange={(newRole) => {
                          if (user.role !== newRole) {
                            posthog.capture("user_role_updated", {
                              userId: user._id,
                              userEmail: user.email,
                              oldRole: user.role,
                              userRole: newRole,
                            });

                            userRoleMutation.mutate(
                              {
                                userId: user._id,
                                role: newRole as EnterpriseProfileType,
                              },
                              {
                                onSuccess: () => {
                                  notify({
                                    variant: "success",
                                    title: "Role updated",
                                    message: "User role updated successfully.",
                                    leadingIcon: (
                                      <CheckCircle color="rgb(var(--base-black))" />
                                    ),
                                  });
                                  refetch();
                                },
                                onError: (error: any) => {
                                  notify({
                                    variant: "error",
                                    title: "Role update failed",
                                    message:
                                      error?.message ||
                                      "Failed to update user role.",
                                  });
                                },
                              },
                            );
                          }
                        }}
                      >
                        <Select.Trigger
                          className="w-full"
                          data-testid={`user-role-trigger-${user._id}`}
                        >
                          <Text as="span" size="1" className="capitalize">
                            {friendlyRole(user.role)}
                          </Text>
                        </Select.Trigger>
                        <Select.Content
                          data-testid={`user-role-options-${user._id}`}
                        >
                          {allowedRoles.map((role) => (
                            <Select.Item
                              key={role}
                              value={role}
                              className="capitalize data-[highlighted]:bg-base-black data-[highlighted]:text-base-white"
                              data-testid={`user-role-option-${user._id}-${role}`}
                            >
                              {friendlyRole(role)}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    )}
                  </Table.Cell>
                  {hasAnyActionAccess && (
                    <Table.Cell className="align-middle">
                      {canArchive && (
                        <Flex gap="2" justify="start">
                          <Tooltip
                            content={
                              user.adminNote
                                ? `Admin note: ${user.adminNote}`
                                : "Add an admin note about this user"
                            }
                          >
                            <Button
                              // Solid when a note exists so whoever manages
                              // users can see which rows are annotated.
                              variant={user.adminNote ? "solid" : "outline"}
                              size="1"
                              onClick={() => setNoteUser(user)}
                              aria-label={
                                user.adminNote
                                  ? `Edit admin note about ${noteSubject}`
                                  : `Add admin note about ${noteSubject}`
                              }
                              data-testid={`user-admin-note-${user._id}`}
                            >
                              <Note size={14} />
                            </Button>
                          </Tooltip>

                          {user.enterpriseProfiles?.find(
                            (profile) =>
                              profile.enterprise ===
                                currentUser?.enterpriseId &&
                              profile.onboarding === "invited",
                          ) && (
                            <Tooltip content="Resend invite">
                              <Button
                                variant="outline"
                                size="1"
                                onClick={() => {
                                  const startTime = performance.now();
                                  resendInviteMutation.mutate(user._id, {
                                    onSuccess: () => {
                                      refetch();
                                      posthog.capture("invite_resent", {
                                        userId: user._id,
                                        userEmail: user.email,
                                        userName: user.name,
                                        userRole: user.role,
                                        durationSeconds:
                                          (performance.now() - startTime) /
                                          1000,
                                      });
                                      notify({
                                        variant: "success",
                                        title: `Invite resent to ${user.name}`,
                                        message:
                                          "A new invite link has been sent.",
                                        leadingIcon: (
                                          <CheckCircle color="rgb(var(--base-black))" />
                                        ),
                                      });
                                    },
                                    onError: (error) => {
                                      posthog.capture("invite_resent_error", {
                                        userId: user._id,
                                        userEmail: user.email,
                                        userName: user.name,
                                        userRole: user.role,
                                        durationSeconds:
                                          (performance.now() - startTime) /
                                          1000,
                                        ...errorAnalyticsPayload(error),
                                      });
                                      notify({
                                        variant: "error",
                                        title: `Failed to resend invite to ${user.name}`,
                                        message: error.message,
                                      });
                                      throw error;
                                    },
                                  });
                                }}
                                disabled={resendInviteMutation.isPending}
                                data-testid={`resend-invite-${user._id}`}
                              >
                                <ArrowClockwise size={14} />
                              </Button>
                            </Tooltip>
                          )}

                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger>
                              <IconButton
                                variant="ghost"
                                color="gray"
                                size="1"
                                disabled={currentUser?.userID === user._id}
                                aria-label={`Actions for ${user.name ?? user.email}`}
                                data-testid={`user-actions-${user._id}`}
                              >
                                <DotsThree size={14} weight="bold" />
                              </IconButton>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content>
                              {reviewDocumentCounts.has(user._id) && (
                                <DropdownMenu.Item
                                  onSelect={() => setResetReviewUser(user)}
                                  data-testid={`reset-custom-review-${user._id}`}
                                >
                                  <Eraser size={14} aria-hidden="true" />
                                  {t("settings.usersRoute.resetReviewMenuItem")}
                                </DropdownMenu.Item>
                              )}
                              <DropdownMenu.Item
                                color="red"
                                onSelect={() => setRemoveDialogUser(user)}
                                data-testid={`remove-from-enterprise-${user._id}`}
                              >
                                <UserMinus size={14} />
                                Remove from enterprise
                              </DropdownMenu.Item>
                              <DropdownMenu.Item
                                color="red"
                                onSelect={() =>
                                  setArchiveEverywhereDialogUser(user)
                                }
                                data-testid={`archive-everywhere-${user._id}`}
                              >
                                <Trash size={14} />
                                Archive everywhere
                              </DropdownMenu.Item>
                            </DropdownMenu.Content>
                          </DropdownMenu.Root>
                        </Flex>
                      )}
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Grid>
      <UserAdminNoteDialog
        user={noteUser}
        onClose={() => setNoteUser(null)}
        onSaved={() => refetch()}
      />
      <ResetCustomReviewDialog
        user={resetReviewUser}
        documentCount={
          resetReviewUser
            ? (reviewDocumentCounts.get(resetReviewUser._id) ?? 0)
            : 0
        }
        onClose={() => setResetReviewUser(null)}
        onReset={() => refetchReviewerProfiles()}
      />

      {/* Remove from Enterprise Confirmation Dialog */}
      <Dialog.Root
        open={!!removeDialogUser}
        onOpenChange={(open) => {
          if (!open) setRemoveDialogUser(null);
        }}
      >
        <Dialog.Content
          className="grid w-[90vw] max-w-lg gap-4"
          data-testid="remove-from-enterprise-dialog"
        >
          <Dialog.Title data-testid="remove-dialog-title">
            Remove <em>{removeDialogUser?.name}</em> from enterprise
          </Dialog.Title>
          <Dialog.Description data-testid="remove-dialog-description">
            This will remove the user from this enterprise only. Their account
            stays active and they can be re-added later. All their data will be
            preserved.
          </Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                data-testid="remove-dialog-cancel"
              >
                {t("common:actions.cancel")}
              </Button>
            </Dialog.Close>
            <Button
              variant="solid"
              color="red"
              disabled={pendingActionUserId === removeDialogUser?._id}
              loading={pendingActionUserId === removeDialogUser?._id}
              data-testid="remove-dialog-confirm"
              onClick={() => {
                if (!removeDialogUser) return;
                const startTime = performance.now();
                setPendingActionUserId(removeDialogUser._id);
                removeFromEnterpriseMutation.mutate(removeDialogUser._id, {
                  onSuccess: () => {
                    setPendingActionUserId(null);
                    refetch();
                    refetchRemovedUsers();
                    setRemoveDialogUser(null);
                    posthog.capture("user_removed_from_enterprise", {
                      userId: removeDialogUser._id,
                      userEmail: removeDialogUser.email,
                      userName: removeDialogUser.name,
                      userRole: removeDialogUser.role,
                      durationSeconds: (performance.now() - startTime) / 1000,
                    });
                    notify({
                      variant: "success",
                      title: `${removeDialogUser.name} removed from enterprise`,
                      message:
                        "User has been removed from this enterprise. They can be restored later.",
                      leadingIcon: (
                        <CheckCircle color="rgb(var(--base-black))" />
                      ),
                    });
                  },
                  onError: (error) => {
                    setPendingActionUserId(null);
                    posthog.capture("user_removed_from_enterprise_error", {
                      userId: removeDialogUser._id,
                      userEmail: removeDialogUser.email,
                      userName: removeDialogUser.name,
                      durationSeconds: (performance.now() - startTime) / 1000,
                      ...errorAnalyticsPayload(error),
                    });
                    notify({
                      variant: "error",
                      title: `Failed to remove ${removeDialogUser.name}`,
                      message: error.message,
                    });
                  },
                });
              }}
            >
              <UserMinus />
              Remove
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Archive Everywhere Confirmation Dialog */}
      <Dialog.Root
        open={!!archiveEverywhereDialogUser}
        onOpenChange={(open) => {
          if (!open) setArchiveEverywhereDialogUser(null);
        }}
      >
        <Dialog.Content
          className="grid w-[90vw] max-w-lg gap-4"
          data-testid="archive-everywhere-dialog"
        >
          <Dialog.Title data-testid="archive-dialog-title">
            Archive <em>{archiveEverywhereDialogUser?.name}</em> everywhere
          </Dialog.Title>
          <Dialog.Description data-testid="archive-dialog-description">
            This will archive the user across all enterprises. Their account
            will be disabled everywhere and they will no longer be able to log
            in.
          </Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                data-testid="archive-dialog-cancel"
              >
                {t("common:actions.cancel")}
              </Button>
            </Dialog.Close>
            <Button
              variant="solid"
              color="red"
              disabled={archiveUserMutation.isPending}
              loading={archiveUserMutation.isPending}
              data-testid="archive-dialog-confirm"
              onClick={() => {
                if (!archiveEverywhereDialogUser) return;
                const startTime = performance.now();
                archiveUserMutation.mutate(archiveEverywhereDialogUser._id, {
                  onSuccess: () => {
                    refetch();
                    setArchiveEverywhereDialogUser(null);
                    posthog.capture("user_archived_everywhere", {
                      userId: archiveEverywhereDialogUser._id,
                      userEmail: archiveEverywhereDialogUser.email,
                      userName: archiveEverywhereDialogUser.name,
                      userRole: archiveEverywhereDialogUser.role,
                      durationSeconds: (performance.now() - startTime) / 1000,
                    });
                    notify({
                      variant: "success",
                      title: `${archiveEverywhereDialogUser.name} archived everywhere`,
                      message: "User has been archived across all enterprises.",
                      leadingIcon: (
                        <CheckCircle color="rgb(var(--base-black))" />
                      ),
                    });
                  },
                  onError: (error) => {
                    posthog.capture("user_archived_everywhere_error", {
                      userId: archiveEverywhereDialogUser._id,
                      userEmail: archiveEverywhereDialogUser.email,
                      userName: archiveEverywhereDialogUser.name,
                      durationSeconds: (performance.now() - startTime) / 1000,
                      ...errorAnalyticsPayload(error),
                    });
                    notify({
                      variant: "error",
                      title: `Failed to archive ${archiveEverywhereDialogUser.name}`,
                      message: error.message,
                    });
                  },
                });
              }}
            >
              <Trash />
              Archive everywhere
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Removed Users Section */}
      {removedUsers && removedUsers.length > 0 && (
        <Grid gap="4" px="2" py="4" data-testid="removed-users-section">
          <Heading
            size="6"
            className="font-sans"
            data-testid="removed-users-heading"
          >
            Removed Users ({removedUsers.length.toLocaleString()})
          </Heading>
          <Text size="3" color="gray" data-testid="removed-users-description">
            These users have been removed from this enterprise. Their accounts
            are still active and they can be restored at any time.
          </Text>
          <Table.Root variant="surface" className="w-full min-w-[300px]">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>
                  {t("common:labels.name")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  {t("common:labels.email")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="w-[200px]">
                  {t("settings.usersRoute.role")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="w-[100px]">
                  {t("common:labels.actions")}
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isRemovedUsersPending && (
                <Table.Row>
                  <Table.Cell colSpan={4} className="text-center">
                    {t("settings.usersRoute.loadingRemovedUsers")}
                  </Table.Cell>
                </Table.Row>
              )}
              {removedUsers.map((user) => (
                <Table.Row
                  key={user._id}
                  className="bg-[#F9F9F9] hover:bg-carbon-50"
                  data-testid={`removed-user-row-${user._id}`}
                >
                  <Table.Cell className="align-middle">
                    <AvatarWithInitials
                      avatar={user?.avatar}
                      name={user?.name}
                      colorKey={user?._id}
                      backgroundColor={user?.avatarBackgroundColor}
                      textColor={user?.avatarTextColor}
                      size={8}
                      square
                    />
                    <Text
                      weight="bold"
                      className="pl-2"
                      data-testid={`removed-user-name-${user._id}`}
                    >
                      {user.name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell
                    className="align-middle"
                    data-testid={`removed-user-email-${user._id}`}
                  >
                    {user.email}
                  </Table.Cell>
                  <Table.Cell
                    className="align-middle capitalize"
                    data-testid={`removed-user-role-${user._id}`}
                  >
                    {friendlyRole(user.role)}
                  </Table.Cell>
                  <Table.Cell className="align-middle">
                    <Button
                      variant="outline"
                      size="1"
                      disabled={pendingActionUserId === user._id}
                      loading={pendingActionUserId === user._id}
                      data-testid={`restore-user-${user._id}`}
                      onClick={() => {
                        const startTime = performance.now();
                        setPendingActionUserId(user._id);
                        restoreToEnterpriseMutation.mutate(user._id, {
                          onSuccess: () => {
                            setPendingActionUserId(null);
                            refetch();
                            refetchRemovedUsers();
                            posthog.capture("user_restored_to_enterprise", {
                              userId: user._id,
                              userEmail: user.email,
                              userName: user.name,
                              userRole: user.role,
                              durationSeconds:
                                (performance.now() - startTime) / 1000,
                            });
                            notify({
                              variant: "success",
                              title: `${user.name} restored to enterprise`,
                              message:
                                "User has been restored to this enterprise.",
                              leadingIcon: (
                                <CheckCircle color="rgb(var(--base-black))" />
                              ),
                            });
                          },
                          onError: (error) => {
                            setPendingActionUserId(null);
                            posthog.capture(
                              "user_restored_to_enterprise_error",
                              {
                                userId: user._id,
                                userEmail: user.email,
                                userName: user.name,
                                durationSeconds:
                                  (performance.now() - startTime) / 1000,
                                ...errorAnalyticsPayload(error),
                              },
                            );
                            notify({
                              variant: "error",
                              title: `Failed to restore ${user.name}`,
                              message: error.message,
                            });
                          },
                        });
                      }}
                    >
                      <ArrowCounterClockwise size={14} />
                      Restore
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Grid>
      )}

      {/* Meaningful Gigs Users Table */}
      {currentUser?.role === EnterpriseProfileType.MEANINGFUL_GIGS && (
        <Grid gap="4" px="2" py="4" data-testid="mg-users-section">
          <Flex justify="between" align="center">
            <Heading
              size="6"
              className="font-sans"
              data-testid="mg-users-heading"
            >
              Internal: Available Meaningful Gigs Users
              {meaningfulGigsUsers?.length
                ? ` (${meaningfulGigsUsers.length.toLocaleString()} users)`
                : ""}
            </Heading>
            <Flex align="center" gap="2">
              <Button
                variant="outline"
                size="2"
                onClick={() => setIsRollbackDialogOpen(true)}
                disabled={rollbackPunttAccessMutation.isPending}
                loading={rollbackPunttAccessMutation.isPending}
                data-testid="rollback-puntt-access-button"
                aria-label={t(
                  "settings.usersRoute.rollbackPunttAccessFromOrphan",
                )}
              >
                <UserMinus />
                Rollback orphan access
              </Button>
              <Button
                variant="outline"
                size="2"
                onClick={() => setIsBackfillDialogOpen(true)}
                disabled={backfillPunttAccessMutation.isPending}
                loading={backfillPunttAccessMutation.isPending}
                data-testid="backfill-puntt-access-button"
              >
                <ArrowClockwise />
                Backfill Puntt access
              </Button>
              <Button
                variant="solid"
                size="2"
                onClick={handleSyncDefaultMgUsers}
                disabled={syncDefaultMgUsersMutation.isPending}
                loading={syncDefaultMgUsersMutation.isPending}
                data-testid="sync-default-mg-users-button"
              >
                <Plus />
                Add All Default MG Users
              </Button>
            </Flex>
          </Flex>

          <Dialog.Root
            open={isRollbackDialogOpen}
            onOpenChange={setIsRollbackDialogOpen}
          >
            <Dialog.Content
              className="grid w-[90vw] max-w-lg gap-4"
              data-testid="rollback-puntt-access-dialog"
              aria-describedby="rollback-puntt-access-description"
            >
              <Dialog.Title data-testid="rollback-puntt-access-title">
                {t("settings.usersRoute.rollbackRosterAccessOrphanEnterprises")}
              </Dialog.Title>
              <Dialog.Description id="rollback-puntt-access-description">
                Removes MEANINGFUL_GIGS profiles for every @puntt.ai and
                @meaningfulgigs.com auto-add roster member on enterprises
                outside the real Puntt customer allowlist (~33 enterprises such
                as Danone, Indeed, and Kenvue). Roster access on those
                allowlisted enterprises is left untouched. Customer accounts are
                never modified.
              </Dialog.Description>
              <Flex gap="3" justify="end">
                <Dialog.Close>
                  <Button
                    variant="soft"
                    color="gray"
                    data-testid="rollback-puntt-access-cancel"
                  >
                    {t("common:actions.cancel")}
                  </Button>
                </Dialog.Close>
                <Button
                  variant="solid"
                  color="red"
                  onClick={handleRollbackPunttAccess}
                  disabled={rollbackPunttAccessMutation.isPending}
                  loading={rollbackPunttAccessMutation.isPending}
                  data-testid="rollback-puntt-access-confirm"
                >
                  <UserMinus />
                  Rollback access
                </Button>
              </Flex>
            </Dialog.Content>
          </Dialog.Root>

          <Dialog.Root
            open={isBackfillDialogOpen}
            onOpenChange={setIsBackfillDialogOpen}
          >
            <Dialog.Content
              className="grid w-[90vw] max-w-lg gap-4"
              data-testid="backfill-puntt-access-dialog"
              aria-describedby="backfill-puntt-access-description"
            >
              <Dialog.Title data-testid="backfill-puntt-access-title">
                {t(
                  "settings.usersRoute.backfillPunttAccessAllowlistedEnterprises",
                )}
              </Dialog.Title>
              <Dialog.Description id="backfill-puntt-access-description">
                Adds every @puntt.ai and @meaningfulgigs.com auto-add roster
                member to each enterprise in the real Puntt customer allowlist
                (~33 enterprises). Orphan and junk enterprises outside that list
                are skipped. Removed users and existing access are left
                untouched.
              </Dialog.Description>
              <Flex gap="3" justify="end">
                <Dialog.Close>
                  <Button
                    variant="soft"
                    color="gray"
                    data-testid="backfill-puntt-access-cancel"
                  >
                    {t("common:actions.cancel")}
                  </Button>
                </Dialog.Close>
                <Button
                  variant="solid"
                  onClick={handleBackfillPunttAccess}
                  disabled={backfillPunttAccessMutation.isPending}
                  loading={backfillPunttAccessMutation.isPending}
                  data-testid="backfill-puntt-access-confirm"
                >
                  <ArrowClockwise />
                  Backfill access
                </Button>
              </Flex>
            </Dialog.Content>
          </Dialog.Root>
          <Text size="3" color="gray">
            Internal admin tool: These @puntt.ai and @meaningfulgigs.com users
            are not yet in your enterprise. You can add them directly or archive
            unwanted accounts. Only visible to MEANINGFUL_GIGS users.
          </Text>
          <Table.Root
            variant="surface"
            className="w-full min-w-[300px]"
            data-testid="mg-users-table"
          >
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell
                  className="w-1/2"
                  data-testid="mg-column-header-name"
                >
                  {t("common:labels.name")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell
                  className="w-1/2"
                  data-testid="mg-column-header-email"
                >
                  {t("common:labels.email")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell
                  className="w-0 text-right"
                  data-testid="mg-column-header-actions"
                >
                  {t("common:labels.actions")}
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isMeaningfulGigsUsersLoading && meaningfulGigsUsersLoader}
              {meaningfulGigsUsers?.map((user) => (
                <Table.Row
                  key={user._id}
                  className="bg-[#F9F9F9] hover:bg-carbon-50"
                >
                  <Table.Cell className="w-1/2 align-middle">
                    <AvatarWithInitials
                      avatar={user?.avatar}
                      name={user?.name}
                      colorKey={user?._id}
                      backgroundColor={user?.avatarBackgroundColor}
                      textColor={user?.avatarTextColor}
                      size={8}
                      square
                    />
                    <Text weight="bold" className="pl-2">
                      {user.name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell className="w-1/2 align-middle">
                    {user.email}
                  </Table.Cell>
                  <Table.Cell className="w-0 align-middle">
                    <Flex gap="2" className="flex-nowrap justify-end">
                      <Button
                        variant="solid"
                        className="shrink-0"
                        size="1"
                        onClick={() =>
                          handleAddUserToEnterprise(
                            user._id,
                            user.name || user.email || "User",
                          )
                        }
                        disabled={addUserToEnterpriseMutation.isPending}
                        loading={addUserToEnterpriseMutation.isPending}
                        data-testid={`add-to-enterprise-${user._id}`}
                      >
                        <Plus />
                        Add to Enterprise
                      </Button>
                      <Button
                        variant="solid"
                        color="red"
                        className="shrink-0"
                        size="1"
                        onClick={() =>
                          handleArchiveMeaningfulGigsUser(
                            user._id,
                            user.name || user.email || "User",
                          )
                        }
                        disabled={archiveMeaningfulGigsUserMutation.isPending}
                        loading={archiveMeaningfulGigsUserMutation.isPending}
                        data-testid={`archive-mg-user-${user._id}`}
                      >
                        <Trash />
                        Archive User
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
              {meaningfulGigsUsers && meaningfulGigsUsers.length === 0 && (
                <Table.Row>
                  <Table.Cell
                    colSpan={3}
                    className="text-gray-50 text-center"
                    data-testid="mg-users-empty-state"
                  >
                    {t("settings.usersRoute.noAvailableMeaningfulGigsUsers")}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Grid>
      )}
    </div>
  );
}
